/**
 * Wire protocol for the party-doc live mutation path.
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/PartyMessages.cs.
 * Both implementations must stay in lockstep — when one changes, the other has
 * to change too.
 *
 * The protocol is intentionally a single full-replace action. The party doc
 * is small (a wallet + a handful of pools), and the conceptual operations
 * players take — "deposit 50gp", "create the bridge fund", "archive the
 * inn-tab" — all imply a coherent doc on the other side. Shipping the whole
 * doc under a version check buys atomic optimistic concurrency without the
 * ceremony of nested path expressions or transactions.
 *
 * Loot rows live in their own SK partition and will get a separate protocol
 * once the loot CRUD slice lands.
 */

import type { Party, BudgetPool, Wallet } from './party';

export const PartyActions = {
  Update: 'party/update',
} as const;

export const PartyServerTypes = {
  PartyPatch:    'party/patch',
  PartyRejected: 'party/rejected',
} as const;

/**
 * Replace-the-whole-party-doc mutation. The client computes the new doc
 * locally — apply the intended change on top of the snapshot it already has —
 * and ships the result. Server overwrites under a version check, bumps
 * version, and broadcasts the new doc to every subscriber.
 *
 * Server-validated invariants on accept (see PartyMessages.cs):
 *   - No pool's currentAmount may be negative on any coin.
 *   - The wallet must cover the coin-wise sum of every non-archived pool's
 *     currentAmount, i.e. unallocated >= 0 on every coin.
 *   - Every pool id must be non-empty and unique within the doc.
 *
 * On a failing invariant the server returns a PartyRejectedPayload with
 * reason="invalid" and the server-current doc instead of silently coercing.
 */
export interface PartyUpdateRequest {
  action: typeof PartyActions.Update;
  expectedVersion: number;
  wallet: Wallet;
  pools: BudgetPool[];
  reason?: string;
}

/**
 * Server-authoritative party-doc echo. Carries the FULL new doc plus the
 * version stamp the client must adopt. The server sends the full doc rather
 * than a diff because clients need to recompute derived fields (unallocated
 * wallet, per-pool progress) anyway and a full doc is the only shape that's
 * idempotent for late joiners.
 */
export interface PartyPatchPayload {
  party: Party;
  version: number;
  updatedAt: string;
}

/**
 * Sent only to the originator on a version race or invariant failure. Carries
 * the server-current party doc so the client can replay its intended change
 * against fresh state.
 */
export interface PartyRejectedPayload {
  reason: 'version_conflict' | 'invalid' | string;
  party: Party | null;
  version: number;
}
